import { useEffect, useState } from 'react';

import { IconButton } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { ArrowDownToLine, Loader } from 'lucide-react';

import { createTanstackTable } from '../common/utils';
import {
  useDownloadAllIFS,
  useDownloadIFS,
  useGetDatasetInputFiles,
} from '../hooks/tanstack/useDatasetsHook';
import { useGetIFS } from '../hooks/tanstack/useIFSHook';
import { useGetRouteParams } from '../hooks/tanstack/useUtilsHook';
import { type IfsStatus } from '../types';
import cn from '../utils/cn';

import TanstackTable from './TanstackTable';

type IFSTableData = {
  id: number;
  inputFile: string | null;
  status: IfsStatus;
  importDate: string | null;
  downloadLink?: string | null;
};

const columnHelper = createColumnHelper<IFSTableData>();

const columns = [
  columnHelper.accessor('inputFile', {
    id: 'inputFile',
    cell: (info) => {
      const id = info.row.original.id;
      const status = info.row.original.status;

      const { mutate: downloadIFS, isPending: isDownloading } =
        useDownloadIFS();

      const { orgId, projectId, datasetId } = useGetRouteParams();

      const isDisabled = status !== 'imported';

      return (
        <div className="text-start flex gap-1 flex-wrap break-all">
          {status === 'imported' && (
            <button
              disabled={isDisabled || isDownloading}
              onClick={() =>
                downloadIFS({
                  orgId,
                  projectId,
                  datasetId,
                  fileId: id,
                })
              }
              className={cn('text-[#2196F3] underline cursor-pointer', {
                'pointer-events-none text-[#B3B3B3]':
                  isDisabled || isDownloading,
              })}
            >
              {info.getValue()}
            </button>
          )}
          {isDownloading && (
            <Loader
              size={22}
              className="animate-spin text-primary-bright-blue"
            />
          )}
          {status === 'not-imported' && <p>{info.getValue()}</p>}
        </div>
      );
    },
    header: () => <div className="flex text-nowrap text-start">Input File</div>,
  }),
  columnHelper.accessor('status', {
    id: 'status',
    cell: (info) => {
      const status = info.getValue();

      const StatusLabel = ({
        label,
        value,
      }: {
        value: 'imported' | 'fail' | 'not-imported';
        label: string;
      }) => (
        <div className="flex justify-start">
          <p
            className={cn(
              'w-fit rounded-full capitalize bg-[#E1FBE7] px-6 py-1 text-green-600',
              {
                'bg-[#E1FBE7] text-green-600': value === 'imported',
                'text-red-600 bg-[#FFF5F2]': value === 'fail',
                'text-[#666] bg-gray-100': value === 'not-imported',
              }
            )}
          >
            {label}
          </p>
        </div>
      );

      switch (status) {
        case 'imported':
          return <StatusLabel value="imported" label="Imported" />;
        case 'fail':
          return <StatusLabel value="fail" label="Fail" />;
        case 'not-imported':
          return <StatusLabel value="not-imported" label="Not Imported" />;
      }
    },
    header: () => <div className="text-start">Status</div>,
  }),
  columnHelper.accessor('importDate', {
    id: 'importDate',
    cell: (info) => (
      <p className="py-1 text-[#333]">
        {info.getValue()
          ? format(info.getValue() as string, 'y/MM/dd hh:mm a')
          : '--'}
      </p>
    ),
    sortingFn: (a, b) =>
      new Date(a.original.importDate as string).getTime() -
      new Date(b.original.importDate as string).getTime(),
    header: () => (
      <div className="flex w-full shrink text-start">Import Date</div>
    ),
  }),
  columnHelper.accessor('downloadLink', {
    id: 'downloadLink',
    cell: (info) => {
      const id = info.row.original.id;
      const status = info.row.original.status;

      const { orgId, projectId, datasetId } = useGetRouteParams();

      const { mutate: downloadIFS, isPending: isDownloading } =
        useDownloadIFS();

      const isDisabled = status !== 'imported';

      return (
        <IconButton
          disabled={isDisabled}
          onClick={() =>
            downloadIFS({
              orgId,
              projectId,
              datasetId,
              fileId: id,
            })
          }
          style={{
            color: isDisabled ? '#B3B3B3' : '#2196f3',
          }}
        >
          {isDownloading ? (
            <Loader size={22} className="animate-spin" />
          ) : (
            <ArrowDownToLine size={22} />
          )}
        </IconButton>
      );
    },
    enableSorting: false,
    header: (info) => {
      const { mutate: downloadAllIFS, isPending: isDownloading } =
        useDownloadAllIFS();

      const { orgId, projectId, datasetId } = useGetRouteParams();

      const { data: datasetInputFiles } = useGetDatasetInputFiles({
        projectId,
        datasetId,
        orgId,
      });

      const isDisabled = datasetInputFiles?.length === 0;

      return (
        <IconButton
          disabled={isDisabled || isDownloading}
          onClick={() =>
            downloadAllIFS({
              orgId,
              projectId,
              datasetId,
            })
          }
          style={{
            color: isDisabled || isDownloading ? '#B3B3B3' : '#2196f3',
          }}
        >
          {isDownloading ? (
            <Loader size={22} className="animate-spin" />
          ) : (
            <ArrowDownToLine size={22} />
          )}
        </IconButton>
      );
    },
  }),
];

export const IFSTable = () => {
  const [data, setData] = useState<IFSTableData[]>([]);

  const { projectId, orgId, datasetId } = useGetRouteParams();

  const table = createTanstackTable<IFSTableData>({
    data,
    columns,
  });

  const {
    data: ifs,
    isLoading: isFetchingIFS,
    isRefetching: isRefetchingIFS,
  } = useGetIFS(
    {
      projectId,
      datasetId,
      orgId,
    },
    {
      enabled: !!datasetId && !!orgId && !!projectId,
    }
  );

  useEffect(() => {
    if (!ifs) {
      return;
    }
    setData(
      ifs.files.map((file) => ({
        id: file.input_file,
        inputFile: file.name,
        status: file.is_imported ? 'imported' : 'not-imported',
        importDate: file.imported_at,
        import: null,
      }))
    );
  }, [ifs]);

  return (
    <TanstackTable
      table={table}
      isLoading={isFetchingIFS || isRefetchingIFS}
      emptyState="No IFS data available.Please generate and upload your files."
    />
  );
};
